// src/utils/api.js
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  timeout: 120000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(config => {
  const key = localStorage.getItem('api_key');
  if (key) config.headers['x-api-key'] = key;
  return config;
});

// ── Auth ───────────────────────────────────────────────────────────────────────
export const login = (email, password) =>
  api.post('/api/auth/login', { email, password }).then(r => r.data);

export const logout = () =>
  api.post('/api/auth/logout').then(r => r.data);

export const getMe = () =>
  api.get('/api/auth/me').then(r => r.data);

// ── Pipeline API ───────────────────────────────────────────────────────────────
export const triggerRun = (opts = {}) =>
  api.post('/api/pipeline/run', opts).then(r => r.data);

export const uploadReport = (file, dryRun = false) => {
  const form = new FormData();
  form.append('report', file);
  form.append('dryRun', dryRun.toString());
  return api.post('/api/pipeline/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  }).then(r => r.data);
};

export const getPipelineStatus = () =>
  api.get('/api/pipeline/status').then(r => r.data);

export const startScheduler = (cron) =>
  api.post('/api/pipeline/scheduler/start', { cron }).then(r => r.data);

export const stopScheduler = () =>
  api.post('/api/pipeline/scheduler/stop').then(r => r.data);

// ── Config API ────────────────────────────────────────────────────────────────
export const getRecipients = () =>
  api.get('/api/config/recipients').then(r => r.data);

export const setRecipients = (emails) =>
  api.put('/api/config/recipients', { emails }).then(r => r.data);

export const addRecipient = (email) =>
  api.post('/api/config/recipients', { email }).then(r => r.data);

export const removeRecipient = (email) =>
  api.delete(`/api/config/recipients/${encodeURIComponent(email)}`).then(r => r.data);

export const getPrompts = () =>
  api.get('/api/config/prompts').then(r => r.data);

export const getPrompt = (key) =>
  api.get(`/api/config/prompts/${key}`).then(r => r.data);

export const setPrompt = (key, promptText) =>
  api.put(`/api/config/prompts/${key}`, { promptText }).then(r => r.data);

export const getRuns = (limit = 50) =>
  api.get(`/api/config/runs?limit=${limit}`).then(r => r.data);

export const getStats = () =>
  api.get('/api/config/stats').then(r => r.data);

export const getHealth = () =>
  api.get('/health/detailed').then(r => r.data);

// ── Analyzed Reports API ──────────────────────────────────────────────────────
export const getReports = (limit = 200) =>
  api.get(`/api/reports?limit=${limit}`).then(r => r.data);

export const getReportOutputUrl = (id) => `/api/reports/${id}/download/output`;
// idx: 0 = primary input, 1+ = extra inputs
export const getReportInputUrl  = (id, idx = 0) => `/api/reports/${id}/download/input/${idx}`;

export const deleteReport = (id) =>
  api.delete(`/api/reports/${id}`).then(r => r.data);

// ── Pending Files API ─────────────────────────────────────────────────────────
export const getPendingFiles = () =>
  api.get('/api/pipeline/pending').then(r => r.data);

export const cancelPendingFile = (id) =>
  api.delete(`/api/pipeline/pending/${id}`).then(r => r.data);

// ── Dext API ──────────────────────────────────────────────────────────────────
export const getDextStatus = () =>
  api.get('/api/dext/status').then(r => r.data);

export const getDextAccounts = () =>
  api.get('/api/dext/accounts').then(r => r.data);

export const getDextTransactions = (accountId, days = 30) =>
  api.get(`/api/dext/accounts/${accountId}/transactions?days=${days}`).then(r => r.data);

export const analyzeDextAccount = ({ accountId, accountName, days = 30, promptKey }) =>
  api.post('/api/dext/analyze', { accountId, accountName, days, promptKey }).then(r => r.data);

export default api;
